<script lang="ts" setup>
import { generateMetaDescription } from '~/utils'

const route = useRoute()
const { translation, bookCode, chapterNumber } = route.params as {
  translation: string
  bookCode: string
  chapterNumber: string
}

const { data: book, error: bookError } = await useBibleBook(translation, bookCode)
const { data: chapter, error: chapterError } = await useBibleChapter(translation, bookCode, chapterNumber)
const { data: books } = useBibleBooks(translation)

if (
  (bookError.value && bookError.value.statusCode === 404) ||
  (chapterError.value && chapterError.value.statusCode === 404)
) {
  throw showError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

watchEffect(() => {
  if (book.value && chapter.value) {
    useSeo({
      title: `Chapter ${chapter.value.chapter} of ${book.value.fullTitle}`,
      description: generateMetaDescription(chapter.value.verses[0]),
    })
  }
})

const nextBook = computed(() => {
  if (!book.value || !books.value) return null
  const bookNumber = book.value.number + 1
  return books.value.find((b) => b.number === bookNumber)
})

const prevBook = computed(() => {
  if (!book.value || !books.value) return null
  const bookNumber = book.value.number - 1
  return books.value.find((b) => b.number === bookNumber)
})
</script>

<template>
  <div v-if="book && chapter" class="space-y-8">
    <PageTitle :href="`/bible/${book.translation}/${book.code}/`">
      {{ book.title }} <span>{{ chapter.chapter }}</span>
    </PageTitle>

    <template v-if="chapter">
      <div v-if="chapter?.audioUrl" class="flex gap-2 items-center">
        <BibleChapterPlaybackButton :book="book" :chapter="chapter" />

        <span class="text-sm">Listen to this chapter • {{ chapter.duration }} min</span>
      </div>

      <div v-if="chapter" class="space-y-6">
        <div v-for="(verse, index) in chapter.verses" :key="verse">
          <div>
            <NuxtLink
              :id="`verse-${index + 1}`"
              :href="`/bible/${book.translation}/${book.code}/${chapter.chapter}#verse-${index + 1}`"
              class="text-zeno-purple"
            >
              [{{ index + 1 }}]
            </NuxtLink>
            {{ verse }}
          </div>
        </div>
      </div>
    </template>

    <div class="flex items-center justify-between text-3xl font-monigue">
      <NuxtLink v-if="chapter.chapter > 1" :href="`/bible/${book.translation}/${book.code}/${chapter.chapter - 1}`">
        &lt; Chapter {{ chapter.chapter - 1 }}
      </NuxtLink>

      <NuxtLink v-else-if="prevBook" :href="`/bible/${prevBook.translation}/${prevBook.code}/1`">
        &lt; {{ prevBook.title }}
      </NuxtLink>

      <NuxtLink
        v-if="chapter.chapter < book.chapterCount"
        :href="`/bible/${book.translation}/${book.code}/${chapter.chapter + 1}`"
        class="ml-auto"
      >
        Chapter {{ chapter.chapter + 1 }} &gt;
      </NuxtLink>

      <NuxtLink v-else-if="nextBook" :href="`/bible/${nextBook.translation}/${nextBook.code}/1`" class="ml-auto">
        {{ nextBook.title }} &gt;
      </NuxtLink>
    </div>
  </div>
</template>

<style lang="postcss" scoped></style>
